import { useState, useEffect, useRef } from 'react';
import Pagination from "../components/Pagination.jsx";
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Edit, Plus, Play, Pause, Eye, Calendar, Tag, Hash, Upload, X, RefreshCw, Video, Loader, AlertCircle, AlertTriangle, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { UploadVideotoallUser, UpdateVideotoallUser, Deletevideotoallusers, GetAllideotoallusers } from '../components/Api.jsx';
import ErrorBoundary from '../components/ErrorBoundary.jsx';

export default function VideoManager() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [playingVideo, setPlayingVideo] = useState(null);
  const [videoLoadingStates, setVideoLoadingStates] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const [fileErrors, setFileErrors] = useState({});

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  const MAX_THUMBNAIL_SIZE = 5 * 1024 * 1024; // 5MB

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchVideos = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const response = await GetAllideotoallusers();

      // Handle the API response structure properly
      if (response && response.data) {
        // Check if response.data is an array or has a videos property
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        } else if (response.data.videos && Array.isArray(response.data.videos)) {
          setVideos(response.data.videos);
        } else if (response.data.globalVideos && Array.isArray(response.data.globalVideos)) {
          setVideos(response.data.globalVideos);
        } else {
          setVideos([]);
        }
      } else {
        setVideos([]);
      }
    } catch (error) {
      setVideos([]);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchVideos(true);
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // Handle escape key to close video
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && playingVideo) {
        setPlayingVideo(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [playingVideo]);

  const onSubmit = async (data) => {
    setIsUploading(true);
    try {
      const videoFile = data.videoFile && data.videoFile[0] ? data.videoFile[0] : null;
      const thumbnailFile = data.thumbnailFile && data.thumbnailFile[0] ? data.thumbnailFile[0] : null;

      if (editingId) {
        await UpdateVideotoallUser(
          editingId,
          data.title,
          data.description || '',
          parseInt(data.sequence),
          data.category || '',
          data.tags || '',
          videoFile,
          thumbnailFile
        );
      } else {
        await UploadVideotoallUser(
          data.title,
          data.description || '',
          parseInt(data.sequence),
          data.category || '',
          data.tags || '',
          videoFile,
          thumbnailFile
        );
      }
      reset();
      setEditingId(null);
      setIsPopupOpen(false);
      showToast(editingId ? 'Video updated successfully' : 'Video uploaded successfully');
      fetchVideos();
    } catch (error) {
      console.error('Error submitting video:', error);
      showToast('Failed to save video. Please try again.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (video) => {
    setEditingId(video.id);
    setSelectedVideoFile(null);
    setSelectedThumbnailFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
    setValue('title', video.title);
    setValue('description', video.description || '');
    setValue('sequence', video.sequence || 1);
    setValue('category', video.category || '');
    setValue('tags', video.tags || '');
    setValue('videoFile', []);
    setValue('thumbnailFile', []);
    setIsPopupOpen(true);
  };

  const handleDelete = (video) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to delete video "${video.title}"? This action cannot be undone and will remove it for all users.`,
      onConfirm: async () => {
        try {
          await Deletevideotoallusers(video.id);
          showToast('Video deleted successfully');
          fetchVideos();
        } catch (error) {
          console.error('Error deleting video:', error);
          showToast('Failed to delete video. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileErrors(prev => ({ ...prev, video: `Video too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (Max 100MB)` }));
        event.target.value = '';
        setSelectedVideoFile(null);
        setValue('videoFile', []);
        return;
      }
      setFileErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.video;
        return newErrors;
      });
      setSelectedVideoFile(file);
      // Also set the value for react-hook-form
      setValue('videoFile', [file]);
    }
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > MAX_THUMBNAIL_SIZE) {
        setFileErrors(prev => ({ ...prev, thumbnail: `Thumbnail too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (Max 5MB)` }));
        event.target.value = '';
        setSelectedThumbnailFile(null);
        setValue('thumbnailFile', []);
        return;
      }
      setFileErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.thumbnail;
        return newErrors;
      });
      setSelectedThumbnailFile(file);
      // Also set the value for react-hook-form
      setValue('thumbnailFile', [file]);
    }
  };

  const handleVideoLoadStart = (videoId) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoId]: 'loading'
    }));
  };

  const handleVideoCanPlay = (videoId) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoId]: 'loaded'
    }));
  };

  const handleVideoError = (videoId) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoId]: 'error'
    }));
  };

  const handlePlayVideo = (videoId) => {
    // Pause any currently playing video
    if (playingVideo && playingVideo !== videoId) {
      const currentVideoElement = document.querySelector(`video[data-video-id="${playingVideo}"]`);
      if (currentVideoElement) {
        currentVideoElement.pause();
      }
    }
    setPlayingVideo(videoId);
  };

  const retryVideoLoad = (videoId) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoId]: 'loading'
    }));
    // Force video reload by updating the src
    const videoElement = document.querySelector(`video[data-video-id="${videoId}"]`);
    if (videoElement) {
      const currentSrc = videoElement.src;
      videoElement.src = '';
      setTimeout(() => {
        videoElement.src = currentSrc;
      }, 100);
    }
  };

  const openPopup = () => {
    setEditingId(null);
    setSelectedVideoFile(null);
    setSelectedThumbnailFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
    reset(); // Reset form for new entry
    setFileErrors({});
    setIsPopupOpen(true);
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br">
        <div className="container p-4">
          {/* Header */}
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-100 p-3 rounded-lg">
                      <Video className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                        Upload the videos to be displayed on the application to all users. Manage global video content, thumbnails, and video metadata for better user engagement.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    aria-label={refreshing ? "Refreshing data" : "Refresh videos"}
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      reset();
                      setSelectedVideoFile(null);
                      setSelectedThumbnailFile(null);
                      setIsPopupOpen(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <Plus className="w-4 h-4" />
                    Upload Video
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Popup Form */}
          {isPopupOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[80vh] overflow-y-auto relative"
              >
                {isUploading && (
                  <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                    <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                    <p className="text-lg font-semibold text-gray-800">
                      Uploading Video...
                    </p>
                  </div>
                )}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-lg">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-800">
                      {editingId ? 'Update Video' : 'Upload Video'}
                    </h2>
                    <button
                      onClick={() => setIsPopupOpen(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
                  {/* Compact row with 3 fields */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Video Title *
                      </label>
                      <input
                        {...register('title', { required: 'Title required' })}
                        placeholder="Title"
                        className="w-full p-2 rounded border border-gray-300 text-black focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      {errors.title && (
                        <p className="text-red-600 text-xs mt-1">{errors.title.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Sequence Number *
                      </label>
                      <input
                        {...register('sequence', {
                          required: 'Sequence required',
                          min: { value: 1, message: 'Sequence must be at least 1' },
                          validate: (value) => {
                            const num = parseInt(value);
                            const duplicate = videos.find(v => v.sequence === num && v.id !== editingId);
                            return !duplicate || 'This Sequence number already exists';
                          }
                        })}
                        type="number"
                        placeholder="1"
                        className="w-full p-2 rounded border border-gray-300 text-black focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      {errors.sequence && (
                        <p className="text-red-600 text-xs mt-1">{errors.sequence.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        {...register('category')}
                        className="w-full p-2 rounded border border-gray-300 text-black focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="" disabled hidden>Select</option>
                        <option value="FITNESS">Fitness</option>
                        <option value="NUTRITION">Nutrition</option>
                        <option value="WELLNESS">Wellness</option>
                        <option value="TRAINING">Training</option>
                        <option value="EDUCATION">Education</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      placeholder="Enter video description"
                      rows={2}
                      className="w-full p-2 rounded border border-gray-300 text-black focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <input
                      {...register('tags')}
                      placeholder="fitness, beginner, exercise"
                      className="w-full p-2 rounded border border-gray-300 text-black focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Video File Upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Video File *
                    </label>

                    {selectedVideoFile ? (
                      <div className="border-2 border-green-300 bg-green-50 rounded p-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Play size={14} className="text-green-600" />
                            <div>
                              <p className="text-xs font-medium text-green-800 truncate max-w-[150px]">{selectedVideoFile.name}</p>
                              <p className="text-xs text-green-600">
                                {(selectedVideoFile.size / (1024 * 1024)).toFixed(1)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedVideoFile(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                              setValue('videoFile', []);
                              setFileErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.video;
                                return newErrors;
                              });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded p-3 text-center hover:border-blue-400 transition-colors">
                        <input
                          {...register('videoFile', {
                            required: editingId ? false : 'Video file is required'
                          })}
                          ref={(e) => {
                            register('videoFile').ref(e);
                            fileInputRef.current = e;
                          }}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          id="videoFile"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="videoFile" className="cursor-pointer">
                          <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                          <p className="text-xs text-gray-600">
                            {editingId ? 'Choose new video (optional)' : 'Click to upload video'}
                          </p>
                          <p className="text-xs text-gray-400">MP4, AVI, MOV supported</p>
                        </label>
                      </div>
                    )}

                    {errors.videoFile && (
                      <p className="text-red-600 text-xs mt-1">{errors.videoFile.message}</p>
                    )}
                    {fileErrors.video && (
                      <p className="text-red-600 text-xs mt-1 font-medium">{fileErrors.video}</p>
                    )}
                  </div>

                  {/* Thumbnail File Upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Thumbnail Image
                    </label>

                    {selectedThumbnailFile ? (
                      <div className="border-2 border-green-300 bg-green-50 rounded p-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-green-800 truncate max-w-[120px]">{selectedThumbnailFile.name}</p>
                              <p className="text-xs text-green-600">
                                {(selectedThumbnailFile.size / (1024 * 1024)).toFixed(1)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedThumbnailFile(null);
                              if (thumbnailInputRef.current) {
                                thumbnailInputRef.current.value = '';
                              }
                              setValue('thumbnailFile', []);
                              setFileErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.thumbnail;
                                return newErrors;
                              });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded p-3 text-center hover:border-blue-400 transition-colors">
                        <input
                          ref={thumbnailInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="thumbnailFile"
                          onChange={handleThumbnailChange}
                        />
                        <label htmlFor="thumbnailFile" className="cursor-pointer">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg mx-auto mb-1 flex items-center justify-center">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-600">
                            {editingId ? 'Choose new thumbnail (optional)' : 'Click to upload thumbnail'}
                          </p>
                          <p className="text-xs text-gray-400">JPG, PNG, GIF, WebP</p>
                        </label>
                      </div>
                    )}

                    {errors.thumbnailFile && (
                      <p className="text-red-600 text-xs mt-1">{errors.thumbnailFile.message}</p>
                    )}
                    {fileErrors.thumbnail && (
                      <p className="text-red-600 text-xs mt-1 font-medium">{fileErrors.thumbnail}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsPopupOpen(false)}
                      className="px-3 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded text-xs transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={Object.keys(fileErrors).length > 0 || isUploading}
                      className={`px-3 py-2 rounded flex items-center gap-1 transition-colors text-xs text-white ${Object.keys(fileErrors).length > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                    >
                      <Upload size={12} />
                      {editingId ? 'Update' : 'Upload'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}



          {/* Enhanced Video List */}
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading videos...</span>
            </div>
          ) : !videos || videos.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center mt-6">
              <div className="flex justify-center mb-4">
                <Video className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No videos found</h3>
              <p className="text-gray-500 max-w-sm mx-auto mb-6">
                Start by uploading your first global video.
              </p>
              <button
                onClick={openPopup}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Upload First Video
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {videos.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                >
                  {/* Video Preview */}
                  <div className="relative aspect-video bg-gray-100">
                    {/* Thumbnail */}
                    {video.thumbnailUrl && (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    )}

                    {/* Video fallback */}
                    {!video.thumbnailUrl && video.videoUrl && (
                      <video
                        src={video.videoUrl}
                        muted
                        loop
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}

                    {/* Play Button */}
                    <button
                      onClick={() => handlePlayVideo(video.id)}
                      className="absolute inset-0 flex items-center justify-center z-10"
                    >
                      <span className="bg-white/80 hover:bg-white rounded-full p-2.5 shadow transition">
                        <Play size={20} className="text-gray-800" />
                      </span>
                    </button>

                    {/* Playing Overlay */}
                    {playingVideo === video.id && video.videoUrl && (
                      <div className="absolute inset-0 bg-black z-20">
                        <video
                          src={video.videoUrl}
                          className="w-full h-full object-contain"
                          controls
                          autoPlay
                        />
                        <button
                          onClick={() => setPlayingVideo(null)}
                          className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}

                    {/* Badges */}
                    <div className="absolute top-1.5 left-1.5 text-[14px] bg-blue-600 text-white px-1.5 py-0.5 rounded">
                      #{video.sequence || 1}
                    </div>
                    <div className="absolute top-1.5 right-1.5 text-[14px] px-1.5 py-0.5 rounded bg-black/60 text-white">
                      {video.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-3">
                    {/* Title + Actions */}
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {video.title}
                      </h3>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(video)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(video)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Description (optional, single line) */}
                    <div className='flex items-center justify-between gap-2 my-1'>
                      <div>
                        {video.description ? (
                          <p className="text-md text-gray-600 mt-0.5">
                            {video.description}
                          </p>
                        ) : <span className='text-md text-gray-600 mt-0.5'>No Description Provided</span>}
                      </div>
                      <div>
                        <p className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm">{video.category}</p>
                      </div>

                    </div>
                    {/* Meta Row */}
                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-600">
                      <div className="flex items-center gap-2">
                        <span>
                          {video.formattedFileSize ||
                            `${(video.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                        <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                        <span>
                          {video.thumbnailUrl ? 'Thumbnail' : 'No Thumbnail'}
                        </span>
                      </div>
                      <span>
                        {video.createdAt &&
                          new Date(video.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </motion.div>

              ))}
            </div>
          )}
        </div>

        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg border ${toast.type === 'error'
            ? 'bg-red-500 text-white border-red-600'
            : 'bg-green-500 text-white border-green-600'
            }`}>
            <div className="flex items-center gap-3">
              {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
              <span className="font-medium">{toast.message}</span>
            </div>
          </div>
        )}
      </div>
      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    
      <div className="mt-6">
        <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(videos) ? videos.length : (videos?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(videos) ? videos.length : (videos?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      </div>

    </ErrorBoundary>
);
}